import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { searchInstrument } from '../../../services/backendAPI';
import { openOrderWindow, openBasketModal } from '../../../store/actions/orders';
import { getEffectiveOrderPayload } from '../../../services/utilities/orderUtils';
import ExchangeTag from '../../Common/ExchangeTag';
import { format } from 'date-fns';
import { styles } from './BasketSearchBarCSS';

const DEBOUNCE_MS = 300;

const BasketSearchBar = ({ currentBasketId, currentBasketName, isExecuted, isGlobalSearch }) => {
  const dispatch = useDispatch();
  const userSettings = useSelector(state => state.profile.userSettings);
  const positionsArray = useSelector(state => state.portfolio?.arrPositionsUpdateData || []);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  const formatExpiryDate = (epochTimestamp) => {
    try {
      const timestamp = epochTimestamp.toString().length === 10
        ? epochTimestamp * 1000
        : epochTimestamp;
      return format(new Date(timestamp), 'dd MMM yyyy');
    } catch (err) {
      return epochTimestamp;
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearching(true);
    try {
      const response = await searchInstrument(query);
      if (response && response.result) {
        // Filter for tradable instruments only (exclude indices if flagged)
        const tradable = response.result.filter(r => {
          const isTradable = r.is_tradable ?? r.tradable ?? true;
          return isTradable !== false && isTradable !== "false";
        });
        setSearchResults(tradable);
        setShowDropdown(true); // Always show, either results or "No results" message
      } else {
        setSearchResults([]);
        setShowDropdown(true); // Show "no results"
      }
    } catch (err) {
      console.error('Basket search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length >= 2) {
      debounceRef.current = setTimeout(() => {
        handleSearch(searchQuery);
      }, DEBOUNCE_MS);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, handleSearch]);

  const handleResultClick = useCallback((result) => {
    // Build the instrument payload matching what MarketWatch passes
    const instrument = {
      token: result.token,
      exchange: result.exchange,
      trading_symbol: result.trading_symbol,
      display_name: result.display_name || result.trading_symbol,
      price_precision: result.price_precision,
      ltp: result.close_price || 0,
      is_tradable: true,
      lotsize: result.board_lot_quantity || 1,
      tick_size: result.tick_size,
    };

    if (isGlobalSearch) {
      dispatch(openBasketModal(instrument));
    } else {
      const orderSide = 'BUY';
      const basePayload = getEffectiveOrderPayload(instrument, orderSide, userSettings, positionsArray);

      const orderData = {
        ...basePayload,
        is_modify: false,
        source: 'BASKET',
        basketDetails: {
          basketId: currentBasketId,
          basketName: currentBasketName,
        },
        quantity: instrument.lotsize || 1,
      };

      dispatch(openOrderWindow(orderData));
    }

    // Clear search
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  }, [dispatch, currentBasketId, currentBasketName, userSettings, positionsArray, isGlobalSearch]);

  const handleClickAway = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, []);

  if (isExecuted) return null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={styles.container}>
        <Box sx={styles.inputBox}>
          <SearchIcon sx={styles.searchIcon} />
          <TextField
            placeholder="Search & add..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchResults.length > 0) setShowDropdown(true);
            }}
            variant="standard"
            fullWidth
            size="small"
            sx={styles.textField}
          />
          {searching && (
            <CircularProgress size={16} sx={styles.spinner} />
          )}
        </Box>

        {/* Search Results Dropdown */}
        {showDropdown && searchQuery.length >= 2 && (
          <Paper elevation={4} sx={styles.dropdown}>
            {searchResults.length > 0 ? (
              searchResults.map((result) => (
                <Box
                  key={`${result.exchange}_${result.token}`}
                  onClick={() => handleResultClick(result)}
                  sx={styles.resultRow}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box component="span" sx={styles.resultName}>
                      {result.display_name}
                      <ExchangeTag exchange={result.exchange} />
                    </Box>
                    <Box component="span" sx={styles.resultSymbol}>
                      {result.exchange === 'CDS' ? result.company : result.trading_symbol}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {result.expiry &&
                     (!result.execution || result.execution !== 'SPREAD') && (
                      <Box component="span" sx={styles.resultExpiry}>
                        Expiry: {formatExpiryDate(result.expiry)}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={styles.noResults}>
                No tradable instruments found
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default BasketSearchBar;
